import { SHOULD_AUTOBATCH } from '@reduxjs/toolkit'
import { Observable, Scheduler, Subject, BehaviorSubject, NEVER } from 'rxjs'
import { switchMap, map } from 'rxjs/operators'
import { AnonymousSubject } from '../utils'
import { RxWebSocket, WebSocketFactory } from './RxWebSocket'
import { RxWebSocketStatus } from './RxWebSocketStatus'

export interface IConnection<TIncoming, TOutgoing> {
  status$: Observable<RxWebSocketStatus>
  incoming$: Observable<TIncoming>
  outgoing$: Subject<TOutgoing>
  connect(endpoint: string, timeoutMS: number): Observable<never>
}

export class Connection<TServerMessage, TClientMessage> implements IConnection<TServerMessage, TClientMessage> {
  private activeSocket: BehaviorSubject<RxWebSocket | null>
  private outgoingSubject: Subject<TClientMessage>
  private encoder = new TextEncoder()
  private decoder = new TextDecoder('utf-8')

  constructor(private scheduler?: Scheduler, private webSocketFactory?: WebSocketFactory) {
    this.activeSocket = new BehaviorSubject(null)
    this.outgoingSubject = new AnonymousSubject<TClientMessage>({
      next: msg => {
        const activeSocket = this.getActiveSocket()
        const dataStr = JSON.stringify(msg)
        const data = this.encoder.encode(dataStr)
        activeSocket.outgoing$.next(data)
      },
      error: (err: any) => {
        const activeSocket = this.getActiveSocket()
        activeSocket.outgoing$.error(err)
      },
      complete: () => {
        const activeSocket = this.getActiveSocket()
        activeSocket.outgoing$.complete()
      }
    })
  }

  public get status$(): Observable<RxWebSocketStatus> {
    return this.activeSocket.pipe(switchMap(socket => (socket ? socket.status$ : NEVER)))
  }

  public get incoming$(): Observable<TServerMessage> {
    return this.activeSocket.pipe(
      switchMap(socket => (socket ? socket.incoming$ : NEVER)),
      map(data => {
        const dataStr = this.decoder.decode(data)
        const dataMsg = JSON.parse(dataStr)
        const { type, ...payload } = dataMsg
        return { type, payload, meta: { [SHOULD_AUTOBATCH]: true } } as any
      })
    )
  }

  public get outgoing$(): Subject<TClientMessage> {
    return this.outgoingSubject
  }

  public connect(endpoint: string, timeoutMS: number): Observable<never> {
    const socket = new RxWebSocket(endpoint, this.scheduler, this.webSocketFactory)
    this.activeSocket.next(socket)
    return socket.connect(timeoutMS)
  }

  private getActiveSocket(): RxWebSocket {
    const activeSocket = this.activeSocket.getValue()

    if (!activeSocket) {
      throw new Error('Socket has not been created')
    }

    return activeSocket
  }
}
